import type { AssertNever } from "@redotech/util/type";
import { AnalyticsSemanticType } from "./analytics-query-constants";

/** Semantic type restrictions and roles. */

/** Types that identify or classify rows: legal as result keys. */
export const AnalyticsDimensionTypes = [
  AnalyticsSemanticType.Id,
  AnalyticsSemanticType.Text,
  AnalyticsSemanticType.Category,
  AnalyticsSemanticType.Boolean,
  AnalyticsSemanticType.Date,
  AnalyticsSemanticType.Datetime,
] as const;

export type AnalyticsDimensionType = (typeof AnalyticsDimensionTypes)[number];

/** Types that quantify rows: legal as aggregate operands, never as keys. */
export const AnalyticsMeasureTypes = [
  AnalyticsSemanticType.Money,
  AnalyticsSemanticType.Quantity,
  AnalyticsSemanticType.Ratio,
  AnalyticsSemanticType.Duration,
] as const;

export type AnalyticsMeasureType = (typeof AnalyticsMeasureTypes)[number];

export const AnalyticsCollectionTypes = [
  AnalyticsSemanticType.List,
  AnalyticsSemanticType.Json,
] as const;

export type AnalyticsCollectionType = (typeof AnalyticsCollectionTypes)[number];

/** Every semantic type has exactly one role. */
export type AnalyticsSemanticTypesArePartitioned = AssertNever<
  | Exclude<
      AnalyticsSemanticType,
      AnalyticsDimensionType | AnalyticsMeasureType | AnalyticsCollectionType
    >
  | Exclude<
      AnalyticsDimensionType | AnalyticsMeasureType | AnalyticsCollectionType,
      AnalyticsSemanticType
    >
>;

export type AnalyticsRole<T extends AnalyticsSemanticType> =
  T extends AnalyticsDimensionType
    ? "dimension"
    : T extends AnalyticsMeasureType
      ? "measure"
      : "collection";

/** Types with a total order the author may sort or range-compare on. */
export const AnalyticsOrderedTypes = [
  AnalyticsSemanticType.Text,
  AnalyticsSemanticType.Category,
  AnalyticsSemanticType.Date,
  AnalyticsSemanticType.Datetime,
  AnalyticsSemanticType.Money,
  AnalyticsSemanticType.Quantity,
  AnalyticsSemanticType.Ratio,
  AnalyticsSemanticType.Duration,
] as const;

export type AnalyticsOrderedType = (typeof AnalyticsOrderedTypes)[number];

/** Types that admit equality. Lists do not. */
export const AnalyticsComparableTypes = [
  ...AnalyticsDimensionTypes,
  ...AnalyticsMeasureTypes,
] as const;

export type AnalyticsComparableType = (typeof AnalyticsComparableTypes)[number];

/** Numeric types that `round`, `floor` and `ceiling` accept. */
export const AnalyticsRoundableTypes = [
  AnalyticsSemanticType.Money,
  AnalyticsSemanticType.Quantity,
  AnalyticsSemanticType.Ratio,
] as const;
